import type { PromptCompletion } from "~/chat/types/chat";
import type { ResponseEvent } from "~/chat/utils/sse-response-reader";
import {
  readResponseChunk,
  SseEventTypes,
} from "~/chat/utils/sse-response-reader";

const UTF8_DECODER = new TextDecoder("utf-8");

function applyUpdate(
  existingCompletion: PromptCompletion,
  update: ResponseEvent,
): PromptCompletion {
  switch (update.event) {
    case SseEventTypes.ANSWER:
      return {
        ...existingCompletion,
        content: existingCompletion.content + update.message,
        streamingStep: undefined,
      };
    case SseEventTypes.SOURCES:
      return {
        ...existingCompletion,
        sources: existingCompletion.sources?.concat(update.sources),
      };
    case SseEventTypes.MESSAGE_ID:
      return {
        ...existingCompletion,
        messageId: update.messageId,
      };
    case SseEventTypes.INFO:
      return {
        ...existingCompletion,
        streamingStep: update.data,
      };
    case SseEventTypes.ERROR:
      return {
        ...existingCompletion,
        error: update.message,
      };
    default:
      return existingCompletion;
  }
}

async function* parseStreamedCompletionResponse(
  response: Response,
): AsyncGenerator<PromptCompletion> {
  let completion: PromptCompletion = {
    content: "",
    sources: [],
    messageId: 0,
  };

  if (!response.ok) {
    const { message = "Request failed" } = await response.json();
    throw new Error(message);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("Response body is not readable");
  }

  let buffer = "";

  function processEvents(events: ResponseEvent[]): boolean {
    const errorEvent = events.find((e) => e.event === SseEventTypes.ERROR);

    if (errorEvent) {
      completion = applyUpdate(completion, errorEvent);
      return true;
    }

    completion = events.reduce(applyUpdate, completion);
    return false;
  }

  try {
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();

      if (value) {
        const decoded = UTF8_DECODER.decode(value, { stream: true });
        buffer += decoded;
      }

      while (buffer.includes("\n\n")) {
        const idx = buffer.indexOf("\n\n");
        const chunk = buffer.slice(0, idx + 2);
        buffer = buffer.slice(idx + 2);

        const events = readResponseChunk(chunk);
        const stop = processEvents(events);

        yield completion;
        if (stop) return;
      }

      if (done) {
        if (buffer.length > 0) {
          const events = readResponseChunk(buffer);
          const stop = processEvents(events);

          yield completion;
          if (stop) return;
        }
        break;
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorName = error instanceof Error ? error.name : "";
    const lowerMessage = errorMessage.toLowerCase();

    if (
      errorName === "AbortError" ||
      errorMessage.includes("BodyStreamBuffer") ||
      lowerMessage.includes("aborted") ||
      errorMessage === "The user aborted a request."
    ) {
      throw new Error("Request canceled");
    }

    throw error instanceof Error ? error : new Error(String(error));
  } finally {
    reader.releaseLock();
  }
}

export { parseStreamedCompletionResponse };
